import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from "react";

type TimerStateContextType = {
  time: number;
  isTimerRunning: boolean;
};

type TimerActionsContextType = {
  startTimer: () => void;
  stopTimer: () => void;
  resetTimer: () => void;
};

const TimerStateContext = createContext<
  TimerStateContextType | undefined
>(undefined);

const TimerActionsContext = createContext<
  TimerActionsContextType | undefined
>(undefined);

export const TimerProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [time, setTime] = useState(0);
  const [isTimerRunning, setIsTimerRunning] = useState(false);

  useEffect(() => {
    if (!isTimerRunning) return;

    const interval = setInterval(() => {
      setTime((prev) => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [isTimerRunning]);

  const actions = useMemo(
    () => ({
      startTimer: () => setIsTimerRunning(true),

      stopTimer: () => setIsTimerRunning(false),

      resetTimer: () => {
        setIsTimerRunning(false);
        setTime(0);
      },
    }),
    []
  );

  const state = useMemo(
    () => ({
      time,
      isTimerRunning,
    }),
    [time, isTimerRunning]
  );

  return (
    <TimerActionsContext.Provider value={actions}>
      <TimerStateContext.Provider value={state}>
        {children}
      </TimerStateContext.Provider>
    </TimerActionsContext.Provider>
  );
};

export const useTimerState = () => {
  const context = useContext(TimerStateContext);

  if (!context) {
    throw new Error("useTimerState must be used within TimerProvider");
  }

  return context;
};

export const useTimerActions = () => {
  const context = useContext(TimerActionsContext);

  if (!context) {
    throw new Error("useTimerActions must be used within TimerProvider");
  }

  return context;
};